import { useTranslation } from 'react-i18next';
/**
 * Material Browser — the BIM-parity replacement for the old cost-override
 * table under Manage > Materials.
 *
 * Left panel: search + project materials grouped by class, usage badges.
 * Right panel: the five identity tabs (Identity / Graphics / Appearance /
 * Physical / Thermal). Toolbar implements the BIM duplicate semantics:
 * Duplicate keeps the appearance asset shared; "Duplicate this asset" and
 * Duplicate > detach option mint an independent render asset.
 */

import { useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import {
    AlertTriangle,
    ChevronDown,
    Download,
    Eraser,
    Link2Off,
    PaintBucket,
    Plus,
    Search,
    Square,
    Trash2,
    Upload,
} from 'lucide-react';
import { useAppStore } from '../../../state/store';
import { useBIMStore } from '../../../state/useBIMStore';
import { MATERIAL_CLASSES, MaterialClass } from '../../../core/MaterialTypes';
import type { BIMMaterial } from '../../../core/types';
import { ManageDialogFrame } from '../ManageDialogFrame';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { AppearanceSwatch, FillPatternPreview } from './materialUiAtoms';
import {
    AppearanceTab,
    GraphicsTab,
    IdentityTab,
    PhysicalTab,
    ThermalTab,
} from './MaterialEditorTabs';
import { MaterialLivePreview } from './MaterialLivePreview';

const MATERIAL_TABS = ['Identity', 'Graphics', 'Appearance', 'Physical', 'Thermal'] as const;
type MaterialTabName = (typeof MATERIAL_TABS)[number];

export function MaterialsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'materials');
    const onClose = useAppStore((state) => state.closeManageDialog);

    const project = useBIMStore((state) => state.project);
    const selection = useBIMStore((state) => state.selection);
    const createMaterial = useBIMStore((state) => state.createMaterial);
    const updateMaterial = useBIMStore((state) => state.updateMaterial);
    const duplicateMaterial = useBIMStore((state) => state.duplicateMaterial);
    const duplicateAppearanceAsset = useBIMStore((state) => state.duplicateAppearanceAsset);
    const updateAppearanceAsset = useBIMStore((state) => state.updateAppearanceAsset);
    const deleteMaterials = useBIMStore((state) => state.deleteMaterials);
    const purgeUnusedMaterials = useBIMStore((state) => state.purgeUnusedMaterials);
    const assignMaterialToElements = useBIMStore((state) => state.assignMaterialToElements);
    const getMaterialUsage = useBIMStore((state) => state.getMaterialUsage);
    const importMaterials = useBIMStore((state) => state.importMaterials);
    const exportMaterials = useBIMStore((state) =>state.exportMaterials); const [selectedId, setSelectedId] = useState<string | null>(null); const [search, setSearch] = useState(''); const [activeTab, setActiveTab] = useState<MaterialTabName>('Identity'); const [purgeNote, setPurgeNote] = useState<string | null>(null); const [pendingDelete, setPendingDelete] = useState<BIMMaterial | null>(null);

    const materials = useMemo(
        () => Array.from(project.materials?.values() ?? []),
        [project],
    );

    // Usage map is recomputed only when the element/material data changes.
    const usageById = useMemo(() => {
        const map = new Map<string, string[]>();
        materials.forEach((m) => map.set(m.id, getMaterialUsage(m)));
        return map;
    }, [materials, getMaterialUsage]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return materials;
        return materials.filter(
            (m) =>
                m.name.toLowerCase().includes(q) ||
                m.identity.class.toLowerCase().includes(q) ||
                m.identity.description.toLowerCase().includes(q),
        );
    }, [materials, search]);

    const grouped = useMemo(() => {
        const byClass = new Map<MaterialClass, BIMMaterial[]>();
        filtered.forEach((m) => {
            const list = byClass.get(m.identity.class) ?? [];
            list.push(m);
            byClass.set(m.identity.class, list);
        });
        // Stable class order, unknown classes last, names alphabetical.
        return MATERIAL_CLASSES.filter((c) => byClass.has(c)).map((c) => [
            c,
            byClass.get(c)!.slice().sort((a, b) => a.name.localeCompare(b.name)),
        ] as const);
    }, [filtered]);

    // Keep selection valid: fall back to first visible material.
    const selected = useMemo(() => {
        const direct = materials.find((m) => m.id === selectedId);
        return direct ?? filtered[0] ?? null;
    }, [materials, selectedId, filtered]);

    const asset = selected ? project.appearanceAssets?.get(selected.appearanceAssetId) : undefined;
    const assetShared =
        !!asset && materials.some((m) => m.id !== selected?.id && m.appearanceAssetId === asset.id);
    const usageCount = selected ? (usageById.get(selected.id) ?? []).length : 0;

    const handleNew = () => {
        const id = createMaterial(`Generic ${materials.length + 1}`);
        setSelectedId(id);
        setActiveTab('Identity');
    };

    const handleDelete = () => {
        if (!selected) return;
        const used = usageById.get(selected.id)?.length ?? 0;
        if (used > 0) {
            // In-use material: confirm through the shared destructive alert.
            setPendingDelete(selected);
            return;
        }
        const nextAfter = materials.find((m) => m.id !== selected.id);
        deleteMaterials([selected.id]);
        setSelectedId(nextAfter?.id ?? null);
    };

    // Confirm path for deleting an in-use material (same logic as before).
    const confirmPendingDelete = () => {
        if (!pendingDelete) return;
        const nextAfter = materials.find((m) => m.id !== pendingDelete.id);
        deleteMaterials([pendingDelete.id]);
        setSelectedId(nextAfter?.id ?? null);
        setPendingDelete(null);
    };

    const handlePurge = () => {
        const removed = purgeUnusedMaterials();
        setPurgeNote(`${removed} unused material(s) purged.`);
        setSelectedId(null);
    };

    // Transfer Project Standards (Materials): export/import a .materials.json payload.
    const handleExportLibrary = () => {
        const payload = JSON.stringify(exportMaterials(), null, 2);
        const blob = new Blob([payload], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project.name || 'project'}-materials.json`;
        a.click();
        URL.revokeObjectURL(url);
        setPurgeNote('Material library exported.');
    };

    const handleImportLibrary = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,application/json';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
                const parsed = JSON.parse(await file.text());
                const imported = importMaterials(parsed);
                setPurgeNote(`${imported} material(s) imported.`);
            } catch {
                setPurgeNote('Import failed — not a valid material library file.');
            }
        };
        input.click();
    };

    const handleApplyToSelection = () => {
        if (!selected || selection.length === 0) return;
        assignMaterialToElements(selection, selected.id);
    };

    return (
        <>
        <ManageDialogFrame
            title={t('misc.materials')}
            isOpen={isOpen}
            onClose={onClose}
            size="4xl"
            footer={
                <HStack w="full" justify="space-between">
                    <HStack spacing={2}>
                        <Button
                            size="xs"
                            leftIcon={<Eraser size={12} />}
                            variant="outline"
                            onClick={handlePurge}
                        >{t('misc.purgeUnused')}</Button>
                        <Tooltip label={t('misc.exportAllMaterialsAppearanceAssetsToALibraryFile')}>
                            <Button size="xs" leftIcon={<Download size={12} />} variant="ghost" onClick={handleExportLibrary}>{t('misc.export')}</Button>
                        </Tooltip>
                        <Tooltip label={t('misc.importAMaterialLibraryFileTransferProjectStandards')}>
                            <Button size="xs" leftIcon={<Upload size={12} />} variant="ghost" onClick={handleImportLibrary}>{t('misc.import')}</Button>
                        </Tooltip>
                        {purgeNote ? (
                            <Text fontSize="xs" color="var(--color-text-muted)">
                                {purgeNote}
                            </Text>
                        ) : null}
                    </HStack>
                    <Button size="sm" variant="ghost" color="var(--color-text-secondary)" onClick={onClose}>{t('misc.close')}</Button>
                </HStack>
            }
        >
            {/* Toolbar */}
            <HStack
                spacing={1}
                pb={2}
                mb={2}
                borderBottom="1px solid var(--color-border)"
                flexShrink={0}
            >
                <Tooltip label={t('misc.newMaterial')}>
                    <IconButton aria-label={t('misc.newMaterial')} icon={<Plus size={14} />} size="sm" variant="ghost" onClick={handleNew} />
                </Tooltip>
                <Menu>
                    <MenuButton as={Button} size="sm" variant="ghost" aria-label={t('misc.duplicate')} rightIcon={<ChevronDown size={12} />}>
                        <Square size={14} />
                    </MenuButton>
                    <MenuList
                        bg="var(--color-bg-tertiary)"
                        borderColor="var(--color-border)"
                        minW="max-content"
                        fontSize="sm"
                    >
                        <MenuItem
                            isDisabled={!selected}
                            onClick={() => selected && setSelectedId(duplicateMaterial(selected.id, false))}
                        >{t('misc.duplicateKeepsSharedAppearanceAsset')}</MenuItem>
                        <MenuItem
                            isDisabled={!selected}
                            onClick={() => selected && setSelectedId(duplicateMaterial(selected.id, true))}
                        >{t('misc.duplicateDetachAppearanceAsset')}</MenuItem>
                    </MenuList>
                </Menu>
                <Tooltip label={assetShared ? 'Duplicate this asset (make appearance independent)' : 'Appearance asset is already independent'}>
                    <Box>
                        <IconButton
                            aria-label={t('misc.duplicateThisAsset')}
                            icon={<Link2Off size={14} />}
                            size="sm"
                            variant="ghost"
                            isDisabled={!selected || !assetShared}
                            onClick={() => selected && duplicateAppearanceAsset(selected.id)}
                        />
                    </Box>
                </Tooltip>
                <Tooltip label={t('misc.deleteMaterial')}>
                    <IconButton
                        aria-label={t('misc.deleteMaterial')}
                        icon={<Trash2 size={14} />}
                        size="sm"
                        variant="ghost"
                        color="var(--color-error)"
                        isDisabled={!selected}
                        onClick={handleDelete}
                    />
                </Tooltip>
                <Box flex="1" />
                <Tooltip label={selection.length > 0 ? `Apply to ${selection.length} selected element(s)` : 'Select wall/floor elements first'}>
                    <Box>
                        <Button
                            size="xs"
                            leftIcon={<PaintBucket size={12} />}
                            variant="outline"
                            isDisabled={!selected || selection.length === 0}
                            onClick={handleApplyToSelection}
                        >{t('misc.applyToSelection')}</Button>
                    </Box>
                </Tooltip>
            </HStack>

            <Box display="flex" gap={4} height="56vh" minH="380px">
                {/* Left: browser tree */}
                <VStack spacing={2} align="stretch" width="280px" flexShrink={0} height="full">
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Search size={12} color="var(--color-text-muted)" />
                        </InputLeftElement>
                        <Input
                            {...{ bg: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}
                            placeholder={t('misc.searchMaterials')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>
                    <Box flex="1" overflowY="auto" pr={1} className="thin-scrollbar">
                        {grouped.length === 0 ? (
                            <Text fontSize="xs" color="var(--color-text-muted)" p={2}>{t('misc.noMaterialsMatchTheSearch')}</Text>
                        ) : null}
                        {grouped.map(([cls, list]) => (
                            <Box key={cls} mb={2}>
                                <Text
                                    fontSize="2xs"
                                    fontWeight="semibold"
                                    textTransform="uppercase"
                                    letterSpacing="0.06em"
                                    color="var(--color-text-muted)"
                                    px={1}
                                    py={0.5}
                                >
                                    {cls}
                                </Text>
                                {list.map((m) => {
                                    const used = usageById.get(m.id)?.length ?? 0;
                                    const isActive = selected?.id === m.id;
                                    return (
                                        <HStack
                                            key={m.id}
                                            spacing={2}
                                            px={1.5}
                                            py={1}
                                            borderRadius="var(--cad-radius-sm)"
                                            cursor="pointer"
                                            bg={isActive ? 'var(--color-accent)' : 'transparent'}
                                            color={isActive ? 'white' : 'var(--color-text-primary)'}
                                            _hover={{ bg: isActive ? 'var(--color-accent)' : 'var(--color-bg-tertiary)' }}
                                            onClick={() => setSelectedId(m.id)}
                                            title={m.identity.description || m.name}
                                        >
                                            <AppearanceSwatch asset={project.appearanceAssets?.get(m.appearanceAssetId)} size={16} />
                                            <Text fontSize="xs" flex="1" noOfLines={1}>
                                                {m.name}
                                            </Text>
                                            {used > 0 ? (
                                                <Badge
                                                    fontSize="2xs"
                                                    px={1}
                                                    borderRadius="var(--cad-radius-sm)"
                                                    bg={isActive ? 'rgba(255,255,255,0.2)' : 'var(--color-bg-tertiary)'}
                                                    color={isActive ? 'white' : 'var(--color-text-muted)'}
                                                >
                                                    {used}
                                                </Badge>
                                            ) : null}
                                        </HStack>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </VStack>

                {/* Right: editor */}
                <VStack spacing={0} flex="1" minW={0} align="stretch" height="full">
                    {!selected ? (
                        <Text fontSize="sm" color="var(--color-text-muted)" p={4}>{t('misc.selectOrCreateAMaterialToEditItsProperties')}</Text>
                    ) : (
                        <>
                            {/* Tab strip */}
                            <HStack
                                spacing={0}
                                flexShrink={0}
                                borderBottom="1px solid var(--color-border)"
                                overflowX="auto"
                                className="thin-scrollbar"
                                flexWrap="nowrap"
                            >
                                {MATERIAL_TABS.map((tab) => (
                                    <Box
                                        key={tab}
                                        as="button"
                                        type="button"
                                        px={4}
                                        py={1.5}
                                        fontSize="xs"
                                        whiteSpace="nowrap"
                                        flexShrink={0}
                                        cursor="pointer"
                                        fontWeight={activeTab === tab ? 'semibold' : 'normal'}
                                        color={activeTab === tab ? 'var(--color-accent-light)' : 'var(--color-text-secondary)'}
                                        borderBottom={activeTab === tab ? '2px solid var(--color-accent)' : '2px solid transparent'}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab}
                                    </Box>
                                ))}
                            </HStack>

                            {/* Tab body + live 3D preview */}
                            <Box flex="1" minH={0} display="flex">
                                <Box flex="1" minW={0} overflowY="auto" p={3}>
                                {/* key remounts editors so typed text resets on material switch */}
                                <Box key={`${selected.id}-${activeTab}`}>
                                    {activeTab === 'Identity' ? (
                                        <IdentityTab
                                            material={selected}
                                            usageCount={usageCount}
                                            patchName={(name) => updateMaterial(selected.id, { name })}
                                            patchIdentity={(patch) => updateMaterial(selected.id, { identity: patch })}
                                        />
                                    ) : null}
                                    {activeTab === 'Graphics' ? (
                                        <GraphicsTab
                                            material={selected}
                                            asset={asset}
                                            patchGraphics={(patch) => updateMaterial(selected.id, { graphics: patch })}
                                        />
                                    ) : null}
                                    {activeTab === 'Appearance' ? (
                                        <AppearanceTab
                                            material={selected}
                                            asset={asset}
                                            assetShared={assetShared}
                                            patchAsset={(patch) => asset && updateAppearanceAsset(asset.id, patch)}
                                            onDuplicateAsset={() => duplicateAppearanceAsset(selected.id)}
                                        />
                                    ) : null}
                                    {activeTab === 'Physical' ? (
                                        <PhysicalTab
                                            material={selected}
                                            patchPhysical={(patch) => updateMaterial(selected.id, { physical: patch })}
                                        />
                                    ) : null}
                                    {activeTab === 'Thermal' ? (
                                        <ThermalTab
                                            material={selected}
                                            patchThermal={(patch) => updateMaterial(selected.id, { thermal: patch })}
                                        />
                                    ) : null}
                                </Box>

                                {/* Graphics quick summary when on Identity */}
                                {activeTab === 'Identity' ? (
                                    <Box mt={4} pt={3} borderTop="1px solid var(--color-border)">
                                        <Text fontSize="2xs" color="var(--color-text-muted)" mb={1}>{t('misc.cutSurfacePatternSummary')}</Text>
                                        <HStack>
                                            <FillPatternPreview
                                                pattern={selected.graphics.cutPatternId}
                                                foreground={selected.graphics.foregroundColor}
                                                background={selected.graphics.backgroundColor}
                                                size={26}
                                            />
                                            <FillPatternPreview
                                                pattern={selected.graphics.surfacePatternId}
                                                foreground={selected.graphics.foregroundColor}
                                                background={selected.graphics.backgroundColor}
                                                size={26}
                                            />
                                            {usageCount > 0 && (
                                                <HStack spacing={1} ml="auto" color="var(--color-text-muted)">
                                                    <AlertTriangle size={11} />
                                                    <Text fontSize="2xs">{t('misc.inUseRenamingTheMaterialUpdatesLayerReferencesLive')}</Text>
                                                </HStack>
                                            )}
                                        </HStack>
                                    </Box>
                                ) : null}
                                </Box>

                                <MaterialLivePreview material={selected} asset={asset} />
                            </Box>
                        </>
                    )}
                </VStack>
            </Box>
        </ManageDialogFrame>

        <ConfirmDialog
            isOpen={pendingDelete !== null}
            onClose={() => setPendingDelete(null)}
            onConfirm={confirmPendingDelete}
            tone="destructive"
            title={t('misc.deleteMaterial')}
            message={
                pendingDelete
                    ? `"${pendingDelete.name}" is used by ${usageById.get(pendingDelete.id)?.length ?? 0} element(s). Deleting it leaves those layers without a material.`
                    : undefined
            }
            confirmLabel="Delete"
        />
        </>
    );
}
